import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces, Trans } from 'react-i18next';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  Elements,
  ElementsConsumer,
} from '@stripe/react-stripe-js';

import { staticURL } from 'core/url';

import {
  InputWrapper,
  WrappedInput,
  Label,
  FormWrapper,
  FormField,
  FormTitle,
  FormDivider,
} from 'libs/forms/styles';

// import ProximaNova from 'assets/fonts/proximanova-regular.woff2';
import { Center } from 'legacyComponents/styled/lib';
import { ProTip } from 'apps/components/shared';
// FIXME: To remove ThemeColors import
import ThemeColors from 'styles/themes/wikifactory/styles/colors';

import Button from 'components/Button';
import { brandIcons, CardLogo } from 'apps/payment/views/creditCard';

import styled from 'styled-components';

import { stripeStore } from 'apps/payment/stripe';

// Docs - https://stripe.com/docs/stripe-js/reference#element-options
const style = {
  // TODO: Replace values by theme values
  base: {
    color: ThemeColors.greyDarker,
    fontFamily: 'ProximaNova',
    fontSmoothing: 'antialiased',
    fontSize: '14px',
    '::placeholder': {
      color: ThemeColors.grey,
      fontSize: '14px',
    },
  },
  invalid: {
    color: ThemeColors.destructive,
    iconColor: ThemeColors.destructive,
  },
};

// NOTE - Fonts won't load unless they come from a HTTPS source
export const fonts = [
  {
    family: 'ProximaNova',
    src: `local("ProximaNova"), local("Proxima Nova"), url("XXX") format("woff2")`,
  },
];

@withNamespaces()
@observer
export class StripeCardForm extends React.Component {
  constructor(props) {
    super(props);
    const { paymentMethodsManager } = props;
    paymentMethodsManager.ResetNewCard();
  }

  onNameChange = e => {
    const {
      paymentMethodsManager: { newCard },
    } = this.props;
    newCard.SetName(e.target.value);
  };

  submit = async () => {
    const {
      paymentMethodsManager: { newCard },
      successAction,
    } = this.props;
    await newCard.Submit(this.props.stripe, this.props.elements);
    if (successAction) successAction();
  };

  render() {
    const { paymentMethodsManager, responsive, t } = this.props;
    const { newCard } = paymentMethodsManager;
    return (
      <>
        <CardsWrapper>
          <Center>
            {Object.keys(brandIcons)
              .slice(0, -1)
              .map((brand, index) => (
                <CardLogo
                  key={index}
                  src={staticURL(
                    `/assets/creditCards/mono/${brandIcons[brand]}`,
                  )}
                  mono
                />
              ))}
          </Center>
        </CardsWrapper>
        <FormWrapper posting={newCard.submitting} responsive={responsive}>
          <FormField>
            <Label>
              <Trans>Name on card</Trans>
            </Label>
            <InputWrapper responsive={responsive}>
              <WrappedInput
                onChange={this.onNameChange}
                value={newCard.name}
                placeholder={t('Exactly as it appears')}
                autoFocus
              />
            </InputWrapper>
          </FormField>
          <FormField>
            <Label>{t('Card number')}</Label>
            <CardNumberElement options={{ style }} />
          </FormField>
          <FormField>
            <Label>{t('Expiry date')}</Label>
            <CardExpiryElement options={{ style }} />
          </FormField>
          <FormField>
            <Label>{t('Security code')}</Label>
            <CardCvcElement options={{ style }} />
          </FormField>
          <ProTip size='small'>
            <Trans>
              <strong>Please note:</strong> your card may be charged a small
              amount for validation purposes.
            </Trans>
          </ProTip>
          <Center>
            {newCard.submitting ? (
              <Button
                text={t('Adding payment method')}
                icon='locked'
                disabled
              />
            ) : (
              <Button
                text={t('Add payment method')}
                icon='locked'
                onClick={() => this.submit()}
              />
            )}
          </Center>
        </FormWrapper>
      </>
    );
  }
}

@observer
export class StripeWrapper extends React.Component {
  render() {
    const { children } = this.props;
    const { stripePromise } = stripeStore;
    // if (!stripe) return null;
    return (
      <StripeElement>
        <Elements options={{ fonts }} stripe={stripePromise}>
          {children}
        </Elements>
      </StripeElement>
    );
  }
}

@observer
export class NewCardForm extends React.Component {
  render() {
    if (!STRIPE_API_KEY) {
      return (
        <div>
          <Trans>
            The app wasn&apos;t built with Stripe support. Use
            --env.STRIPE_API_KEY=&apos;apikey&apos;
          </Trans>
        </div>
      );
    }
    const { paymentMethodsManager, responsive } = this.props;
    return (
      <>
        <FormTitle>
          <Trans>Add a new payment method</Trans>:
        </FormTitle>
        <FormDivider />
        <StripeWrapper>
          <ElementsConsumer>
            {({ stripe, elements }) => (
              <StripeCardForm
                paymentMethodsManager={paymentMethodsManager}
                responsive={responsive}
                stripe={stripe}
                elements={elements}
              />
            )}
          </ElementsConsumer>
        </StripeWrapper>
      </>
    );
  }
}

StripeCardForm.propTypes = {
  paymentMethodsManager: PropTypes.object,
  stripe: PropTypes.object,
  responsive: PropTypes.bool,
  successAction: PropTypes.func,
  t: PropTypes.func,
};

StripeWrapper.propTypes = {
  children: PropTypes.node,
};

NewCardForm.propTypes = {
  paymentMethodsManager: PropTypes.object,
  responsive: PropTypes.bool,
};

const StripeElement = styled.div`
  .StripeElement {
    height: 40px;
    padding: 11px 10px 12px 10px;
    border: 1px solid ${props => props.theme.color.wfGrey200};
    border-radius: 2px;
    background-color: ${props => props.theme.color.wfWhite};
    margin: 5px 0 15px 0;
    -webkit-transition: box-shadow 150ms ease;
    transition: box-shadow 150ms ease;
  }
  .StripeElement--focus {
    border: 1px solid ${props => props.theme.color.wfGrey200};
  }
  .StripeElement--invalid {
    border-color: ${props => props.theme.color.wfOrange};
  }
  .StripeElement--webkit-autofill {
    background-color: #fefde5 !important;
  }
`;

const CardsWrapper = styled.div`
  margin-top: 20px;
  margin-bottom: 10px;
`;
